import { useState, useEffect } from "react";
import useProfileStore from "../../../store/builders/useProfileStore";
import { toast } from "react-hot-toast";
import { axiosInstance } from "../../../lib/axiosInstance";
import { Loader2, CheckCircle } from "lucide-react";

const Documentation = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const { profile, fetchProfile, updateProfile, isLoading, isUpdating } =
    useProfileStore();

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocName, setSelectedDocName] = useState("");

  const [isVerifyingGst, setIsVerifyingGst] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile?.builder && !isEditing) {
      setFormData({
        qualification: profile.builder.qualification || "",
        gst_number: profile.builder.gst_number || "",
        aadhar_number: profile.builder.aadhar_number || "",
        government_license: profile.builder.government_license || null,
        graduation_certificate: profile.builder.graduation_certificate || null,
      });
      // Set verified status if GST exists and presumably was verified during signup or previously
      if (profile.builder.gst_number) {
        setGstVerified(true);
      }
    }
  }, [profile, isEditing]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (key, value) => {
    let processedValue = value;
    if (key === "gst_number") {
      processedValue = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .substring(0, 15);

      if (gstVerified && processedValue !== formData.gst_number) {
        setGstVerified(false);
      }
    }
    setFormData((prev) => ({ ...prev, [key]: processedValue }));
  };

  const verifyGST = async () => {
    if (!formData.gst_number || formData.gst_number.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN.");
      return;
    }
    setIsVerifyingGst(true);
    try {
      const response = await axiosInstance.post("/builders/auth/gstin/verify", {
        gstin: formData.gst_number,
      });
      if (
        response.data.message === "GSTIN verified successfully." &&
        response.data.details
      ) {
        setGstVerified(true);
        toast.success("GSTIN verified successfully!");
      } else {
        toast.error(response.data.message || "GSTIN verification failed.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "GSTIN verification request failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingGst(false);
    }
  };

  const handleSubmit = async () => {
    if (formData.gst_number && !gstVerified) {
      toast.error("Please verify your GSTIN before saving.");
      return;
    }

    try {
      const payload = {
        qualification: formData.qualification,
        gst_number: formData.gst_number,
        aadhar_number: formData.aadhar_number,
      };

      const success = await updateProfile(payload);
      if (success) {
        toggleEdit();
      }
    } catch (error) {
      console.error("Error updating documentation:", error);
    }
  };

  const handleViewDocument = (docKey) => {
    setSelectedDoc(profile?.builder?.[docKey] || null);
    setSelectedDocName(formatLabel(docKey));
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const isImage = (url) => /\.(jpg|jpeg|png|webp)$/i.test(url);
  const isPdf = (url) => /\.pdf$/i.test(url);

  const regularFields = ["qualification", "gst_number", "aadhar_number"];
  const documentFields = ["government_license", "graduation_certificate"];

  return (
    <div className="py-2 text-xs">
      {/* Header */}
      <div className="flex items-center justify-start gap-4 mb-4 border-b border-gray-400 pb-3">
        <h2 className="text-sm font-semibold">Documentation</h2>
        <div className="flex items-center gap-2">
          {isEditing && (
            <button
              onClick={handleSubmit}
              disabled={isUpdating}
              className="px-3 py-1.5 bg-green-500 text-white rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
          )}
          <button
            onClick={toggleEdit}
            disabled={isUpdating}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md text-xs transition-colors"
          >
            {isEditing ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {regularFields.map((key) => {
          const isGst = key === "gst_number";
          const isAadhaar = key === "aadhar_number";

          return (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {formatLabel(key)}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={formData[key] || ""}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    disabled={!isEditing || isUpdating || isAadhaar}
                    className={`w-full p-2 rounded-md text-xs transition-colors outline-none ${isEditing && !isAadhaar
                      ? (formData[key] || (isGst && !formData.gst_number) ? "border border-gray-300 bg-white" : "border border-red-500 bg-white focus:ring-1 focus:ring-red-100")
                      : !formData[key] && key === 'qualification'
                        ? "bg-red-50 border border-red-500 text-red-600 font-medium"
                        : "bg-gray-100 border-none text-gray-600 font-medium"
                      }`}
                  />
                  {isGst && gstVerified && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                  )}
                </div>
                {isGst && isEditing && (
                  <button
                    onClick={verifyGST}
                    disabled={isVerifyingGst || gstVerified || !formData.gst_number}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[60px]"
                  >
                    {isVerifyingGst ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : gstVerified ? (
                      "Verified"
                    ) : (
                      "Verify"
                    )}
                  </button>
                )}
              </div>
              {isGst && gstVerified && (
                <div className="mt-1">
                  <span className="inline-flex items-center bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                    GSTIN VERIFIED
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {documentFields.map((key) => (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {formatLabel(key)}
            </label>
            <button
              onClick={() => handleViewDocument(key)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md text-xs w-full sm:w-auto"
            >
              View Document
            </button>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl p-4 shadow-lg">
            <h3 className="text-sm font-semibold mb-3">{selectedDocName}</h3>

            <div className="w-full h-[70vh] bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
              {!selectedDoc && (
                <p className="text-xs text-gray-600">No document available</p>
              )}

              {selectedDoc && isImage(selectedDoc) && (
                <img
                  src={selectedDoc}
                  alt={selectedDocName}
                  className="max-w-full max-h-full object-contain"
                />
              )}

              {selectedDoc && isPdf(selectedDoc) && (
                <iframe
                  src={selectedDoc}
                  title="PDF Preview"
                  className="w-full h-full"
                />
              )}
            </div>

            <button
              onClick={closeModal}
              className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded-md text-xs"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const formatLabel = (key) => {
  if (key === 'gst_number') return 'GST Number';
  if (key === 'aadhar_number') return 'Aadhar Number';
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export default Documentation;
